import { ArrowLeft, Camera, Eye, Loader2, Shield, Users, X, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppState } from '../../context/AppStateContext';
import { API_CONFIG } from '../../config/api';
import { Badge } from '../common/Badge';
import { ui } from '../../styles/ui';
import { getVerificationDisplayStatus, VERIFICATION_STATUS_ICON, VERIFICATION_STATUS_LABEL, VERIFICATION_STATUS_TONE } from '../../utils/verificationStatus';

const CHAIN_STATUS_TONE = { verified: 'success', pending: 'warning', rejected: 'danger' };

export const ProductDetailsPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { products, loadProducts, apiCall } = useAppState();
  const product = products.find((p) => p._id === productId);

  useEffect(() => {
    if (!product) loadProducts(undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Enrollment photo(s) captured by the Texture App, plus the most recent
  // Customer/User verification photo (if any cleared the quality gate) —
  // so an admin can visually compare what was enrolled against what a
  // customer actually scanned, not just the abstract match score.
  const [textureImages, setTextureImages] = useState(null);
  const [textureImagesLoading, setTextureImagesLoading] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    if (!product?.barcode) return;
    setTextureImagesLoading(true);
    apiCall(API_CONFIG.endpoints.textureImages(product.barcode))
      .then((res) => setTextureImages(res?.data || null))
      .catch(() => setTextureImages(null))
      .finally(() => setTextureImagesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.barcode]);

  useEffect(() => {
    if (!lightboxImage) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') setLightboxImage(null); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxImage]);

  const handleBack = () => navigate(-1);

  if (!product) {
    return (
      <div className="space-y-4 max-w-4xl">
        <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div className={`${ui.card} p-8 text-center text-gray-500 dark:text-gray-400`}>
          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
          Loading product...
        </div>
      </div>
    );
  }

  const displayStatus = getVerificationDisplayStatus(product);
  const StatusIcon = VERIFICATION_STATUS_ICON[displayStatus] || VERIFICATION_STATUS_ICON.default;

  return (
    <div className="space-y-4 max-w-4xl">
      <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      <div className={`${ui.card} p-5`}>
        <h1 className="text-base font-semibold text-gray-900 dark:text-white mb-5">{product.name}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Product Information</h4>
            <dl className="space-y-2.5 text-sm">
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Category</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{product.category}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Description</dt>
                <dd className="text-gray-700 dark:text-gray-300">{product.description || 'No description available'}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Batch Number</dt>
                <dd className="font-mono font-medium text-gray-900 dark:text-white">{product.batchNo}</dd>
              </div>
            </dl>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Inventory & Pricing</h4>
            <div className="text-2xl font-semibold text-gray-900 dark:text-white">${product.price}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">Unit Price</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-2.5 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">{product.quantity}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Quantity</div>
              </div>
              <div className="p-2.5 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">{product.unit}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Unit</div>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Barcode & Status</h4>
            <Badge tone={VERIFICATION_STATUS_TONE[displayStatus]} icon={StatusIcon} spin={displayStatus === 'in_progress'} className="mb-3">
              {VERIFICATION_STATUS_LABEL[displayStatus]}
            </Badge>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Barcode</div>
            <code className="block px-2.5 py-1.5 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono font-semibold text-center break-all mb-3">{product.barcode}</code>
            {product.barcodeImageUrl && (
              <div className="flex justify-center">
                <img
                  crossOrigin="anonymous"
                  src={product.barcodeImageUrl}
                  alt="Barcode"
                  className="max-w-full h-auto bg-white p-2 rounded-md border border-gray-200 dark:border-gray-600"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </div>
        </div>

        {product.verificationChain && product.verificationChain.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 mt-4">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Verification Chain</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {product.verificationChain.map((verification, index) => {
                const roleIcons = { 'Field Officer': Shield, Dealer: Users, User: Eye };
                const RoleIcon = roleIcons[verification.role] || Shield;
                return (
                  <div key={index} className="flex items-center p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-100 dark:border-gray-700">
                    <RoleIcon className="h-4 w-4 text-gray-400 mr-3 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{verification.role}</div>
                      <Badge tone={CHAIN_STATUS_TONE[verification.status] || 'neutral'} className="mt-1 capitalize">{verification.status}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {(textureImagesLoading || textureImages?.enrollmentSamples?.length > 0 || textureImages?.customerVerification) && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 mt-4">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Camera className="h-3.5 w-3.5" />
              Texture Verification
            </h4>

            {textureImagesLoading ? (
              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2 py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading texture captures...
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Enrolled by Texture App{textureImages?.enrollmentSamples?.length > 1 ? ` (${textureImages.enrollmentSamples.length} samples)` : ''}
                  </div>
                  {textureImages?.enrollmentSamples?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {textureImages.enrollmentSamples.map((sample, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setLightboxImage(sample.imageUrl)}
                          className="p-0 border-0 bg-transparent cursor-pointer"
                          title={`Quality score ${sample.qualityScore ?? '—'} · ${sample.capturedAt ? new Date(sample.capturedAt).toLocaleString() : ''} — click to view full size`}
                        >
                          <img
                            crossOrigin="anonymous"
                            src={sample.imageUrl}
                            alt={`Enrollment sample ${i + 1}`}
                            className="w-28 h-28 object-cover rounded-md border border-gray-200 dark:border-gray-600 hover:opacity-80 hover:ring-2 hover:ring-blue-500 transition"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400 dark:text-gray-500 italic">Not enrolled yet</div>
                  )}
                </div>

                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Latest Customer Verification</div>
                  {textureImages?.customerVerification ? (
                    <div>
                      <button
                        type="button"
                        onClick={() => setLightboxImage(textureImages.customerVerification.imageUrl)}
                        className="p-0 border-0 bg-transparent cursor-pointer block mb-2"
                        title="Click to view full size"
                      >
                        <img
                          crossOrigin="anonymous"
                          src={textureImages.customerVerification.imageUrl}
                          alt="Customer verification capture"
                          className="w-28 h-28 object-cover rounded-md border border-gray-200 dark:border-gray-600 hover:opacity-80 hover:ring-2 hover:ring-blue-500 transition"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </button>
                      <Badge tone={textureImages.customerVerification.result === 'SUCCESS' ? 'success' : 'danger'}>
                        {textureImages.customerVerification.result === 'SUCCESS' ? 'VERIFIED' : 'NOT VERIFIED'}
                        {textureImages.customerVerification.matchScore != null ? ` · ${textureImages.customerVerification.matchScore}% match` : ''}
                      </Badge>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400 dark:text-gray-500 italic">No customer verification yet</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {product.verificationStatus === 'rejected' && product.rejectionReason && (
          <div className="border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/10 rounded-lg p-4 mt-4">
            <h4 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1.5 flex items-center gap-1.5">
              <XCircle className="h-4 w-4" />
              Rejection Reason
            </h4>
            <p className="text-sm text-red-700 dark:text-red-300">{product.rejectionReason}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mt-4">
          <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <div className="text-xs text-gray-500 dark:text-gray-400">Created</div>
            <div className="font-medium text-gray-900 dark:text-white">{new Date(product.createdAt).toLocaleString()}</div>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <div className="text-xs text-gray-500 dark:text-gray-400">Last Updated</div>
            <div className="font-medium text-gray-900 dark:text-white">{new Date(product.updatedAt).toLocaleString()}</div>
          </div>
        </div>
      </div>

      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightboxImage(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            crossOrigin="anonymous"
            src={lightboxImage}
            alt="Texture capture full size"
            className="max-w-full max-h-full rounded-lg shadow-2xl cursor-default"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};
